import type {
  HPKECryptoScheme,
  HPKESchemeInfo,
} from 'metaserver/static/js/encrypted_folder/crypto/hpke/hpkeSuite';
import type {
  AsymmetricKeyPair,
  PrivateKey,
  PublicKey,
} from 'metaserver/static/js/encrypted_folder/crypto/hpke/types';
import {
  EntropyLimitReached,
  InvalidKeyError,
  InvalidKeyFormatError,
  UnknownError,
} from 'metaserver/static/js/encrypted_folder/crypto/errors';

const nDH = 32;
const webcryptoHPKEInfo = {
  nSecret: 32,
  nK: 32,
  nN: 12,
  nEnc: 65,
  kdfId: 0x0001,
  kemId: 0x10,
  aeadId: 0x0002,
};

export const WebcryptoHPKESuite: HPKECryptoScheme = {
  async generateKeyPair(): Promise<AsymmetricKeyPair> {
    const keyPair = await crypto.subtle
      .generateKey(
        {
          name: 'ECDH',
          namedCurve: 'P-256',
        },
        true,
        ['deriveBits']
      )
      .catch((e) => {
        throw new UnknownError(e.message);
      });
    return {
      publicKey: keyPair.publicKey,
      privateKey: keyPair.privateKey,
    };
  },

  async sealWithKey(props: {
    key: Uint8Array<ArrayBuffer>;
    nonce: Uint8Array<ArrayBuffer>;
    aad: Uint8Array<ArrayBuffer>;
    pt: Uint8Array<ArrayBuffer>;
  }): Promise<Uint8Array<ArrayBuffer>> {
    const {key, nonce, aad, pt} = props;
    const encryptionCryptoKey = await crypto.subtle
      .importKey('raw', key, {name: 'AES-GCM'}, true, ['encrypt', 'decrypt'])
      .catch(() => {
        throw new InvalidKeyFormatError();
      });

    const cipherText = await crypto.subtle
      .encrypt(
        {
          name: 'AES-GCM',
          iv: nonce,
          additionalData: aad,
        },
        encryptionCryptoKey,
        pt
      )
      .catch((e) => {
        throw new UnknownError(e.message);
      });

    return new Uint8Array(cipherText);
  },

  async openWithKey(props: {
    key: Uint8Array<ArrayBuffer>;
    nonce: Uint8Array<ArrayBuffer>;
    aad: Uint8Array<ArrayBuffer>;
    ct: Uint8Array<ArrayBuffer>;
  }): Promise<Uint8Array<ArrayBuffer>> {
    const {key, nonce, aad, ct} = props;
    const encryptionCryptoKey = await crypto.subtle
      .importKey('raw', key, {name: 'AES-GCM'}, true, ['encrypt', 'decrypt'])
      .catch(() => {
        throw new InvalidKeyFormatError();
      });

    const plainText = await crypto.subtle
      .decrypt(
        {
          name: 'AES-GCM',
          iv: nonce,
          additionalData: aad,
        },
        encryptionCryptoKey,
        ct
      )
      .catch(() => {
        throw new InvalidKeyError();
      });

    return new Uint8Array(plainText);
  },

  async serializePublicKey(pkX: CryptoKey): Promise<Uint8Array<ArrayBuffer>> {
    const exportedKey = await crypto.subtle.exportKey('raw', pkX).catch((e) => {
      throw new UnknownError(e.message);
    });
    return new Uint8Array(exportedKey);
  },

  async deserializePublicKey(pkXm: Uint8Array<ArrayBuffer>): Promise<CryptoKey> {
    return await crypto.subtle
      .importKey('raw', pkXm, {name: 'ECDH', namedCurve: 'P-256'}, true, [])
      .catch(() => {
        throw new InvalidKeyFormatError();
      });
  },

  async extract(
    salt: Uint8Array<ArrayBuffer>,
    ikm: Uint8Array<ArrayBuffer>
  ): Promise<Uint8Array<ArrayBuffer>> {
    const emptySalt = new Uint8Array(32).fill(0);

    let saltArray;
    if (salt === undefined || salt.length === 0) {
      saltArray = emptySalt;
    } else {
      saltArray = salt;
    }
    const length = saltArray.length * 8;
    const key = await crypto.subtle
      .importKey(
        'raw',
        saltArray,
        {
          name: 'HMAC',
          hash: 'SHA-256',
          length: length,
        },
        false,
        ['sign']
      )
      .catch(() => {
        throw new InvalidKeyFormatError();
      });

    return new Uint8Array(
      await crypto.subtle.sign('HMAC', key, ikm).catch((e) => {
        throw new UnknownError(e.message);
      })
    );
  },

  async expand(
    prk: Uint8Array<ArrayBuffer>,
    info: Uint8Array<ArrayBuffer>,
    length: number
  ): Promise<Uint8Array<ArrayBuffer>> {
    const hmacKey = await crypto.subtle
      .importKey(
        'raw',
        prk,
        {
          name: 'HMAC',
          hash: 'SHA-256',
          length: 256,
        },
        false,
        ['sign']
      )
      .catch(() => {
        throw new InvalidKeyFormatError();
      });
    const outputBytes = new Uint8Array(new ArrayBuffer(length));
    let previousBytes = new Uint8Array(0);
    const intermediateBytes = info ?? new Uint8Array(0);
    if (length > 255 * 256) {
      throw new EntropyLimitReached();
    }
    const tmpBufferLength = 256 + intermediateBytes.length + 1;
    const tmpBuffer = new Uint8Array(tmpBufferLength);
    for (let suffixByte = 1, currentLength = 0; currentLength < outputBytes.length; suffixByte++) {
      tmpBuffer.set(previousBytes, 0);
      tmpBuffer.set(intermediateBytes, previousBytes.length);
      tmpBuffer[previousBytes.length + intermediateBytes.length] = suffixByte;
      const endOfData = previousBytes.length + intermediateBytes.length + 1;
      previousBytes = new Uint8Array(
        await crypto.subtle.sign('HMAC', hmacKey, tmpBuffer.slice(0, endOfData)).catch((e) => {
          throw new UnknownError(e.message);
        })
      );
      if (outputBytes.length - currentLength >= previousBytes.length) {
        outputBytes.set(previousBytes, currentLength);
        currentLength += previousBytes.length;
      } else {
        outputBytes.set(previousBytes.slice(0, outputBytes.length - currentLength), currentLength);
        currentLength += outputBytes.length - currentLength;
      }
    }

    return outputBytes;
  },

  async dh(skX: PrivateKey, pkY: PublicKey): Promise<Uint8Array<ArrayBuffer>> {
    const derivedKey = await crypto.subtle
      .deriveBits(
        {
          name: 'ECDH',
          public: pkY,
        },
        skX,
        nDH * 8
      )
      .catch((e) => {
        throw new UnknownError(e.message);
      });
    return new Uint8Array(derivedKey);
  },

  async pkFromSk(sk: PrivateKey): Promise<PublicKey> {
    // https://stackoverflow.com/a/72153942
    const jwkPrivate = await crypto.subtle.exportKey('jwk', sk).catch((e) => {
      throw new UnknownError(e.message);
    });
    delete jwkPrivate.d;
    return crypto.subtle
      .importKey('jwk', jwkPrivate, {name: 'ECDH', namedCurve: 'P-256'}, true, [])
      .catch(() => {
        throw new InvalidKeyFormatError();
      });
  },

  info(): HPKESchemeInfo {
    return webcryptoHPKEInfo;
  },

  async sha256(data: Uint8Array<ArrayBuffer>): Promise<Uint8Array> {
    return new Uint8Array(await crypto.subtle.digest('SHA-256', data));
  },
};
